import * as React from "react";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import { Helmet } from "react-helmet";
import { withToastManager } from "react-toast-notifications";

/** GraphQL */
import { getCompanyReports } from "../../graphql/custom-queries";
import { listCategorys } from "../../graphql/queries";
import { Province, Suburb } from "../../API";

/** Presentation/UI */
import Table from "../../Components/Table";
import StyledButton from "../../Components/Styled/Button";
import BackendWrapper from "../../Components/Layouts/BackendWrapper";
import PageLoader from "../../Components/PageLoader";
import {
  TableHeader,
  TableHeaderContainer
} from "../../Components/Styled/ListViewElements";
import Row from "../../Components/Styled/Row";
import Card from "../../Components/Styled/Card";

/** Themes */
import { Colors } from "../../Themes";

/** Custom Types */
import { ToastNotificationType } from "../../CustomTypes";
import { TableConsts } from "src/Utils/Consts";
import { Col, Label, Input } from "reactstrap";
import Select from "react-select";
import { getUserId, formatDropdown } from "src/Utils/Helpers";

type SelectType = {
  value: string;
  label: string;
};

type SelectProvinceType = {
  value: Province;
  label: string;
};

type SelectSuburbType = {
  value: Suburb;
  label: string;
};

type Props = {
  toastManager: ToastNotificationType;
};

type State = {
  importModal: boolean;
  searchFilter: boolean;
  keywordSearch: string;
  province: SelectProvinceType;
  suburb: SelectSuburbType;
  category: SelectType;
  city: string;
};

class PurchasedReports extends React.Component<Props> {
  state: State = {
    importModal: false,
    searchFilter: false,
    keywordSearch: "",
    province: { value: Province.GAUTENG, label: "" },
    suburb: { value: Suburb.SUBURB_ONE, label: "" },
    category: { value: "", label: "" },
    city: ""
  };

  // Select Category
  selectCategory = (category: SelectType): void => {
    this.setState({ category });
  };

  // Select province
  selectProvince = (province: SelectProvinceType): void => {
    this.setState({ province });
  };

  // format constants into dropdowns for provinces
  listOfProvinces = (consts: {}) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  // Select suburb
  selectSuburb = (suburb: SelectSuburbType): void => {
    this.setState({ suburb });
  };

  // format constant into dropdowns for suburbs
  listOfSuburbs = (consts: {}) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  toggleSearchFilter = (): void => {
    this.setState({ searchFilter: !this.state.searchFilter });
  };

  /** Success notification */
  toastNotification = (message: string, appearance?: string) => {
    this.props.toastManager.add(message, {
      appearance: appearance ? appearance : "success",
      autoDismiss: true
    });
  };

  render() {
    const { keywordSearch, province, suburb, category, city } = this.state;
    return (
      <BackendWrapper>
        <Query query={gql(listCategorys)}>
          {({ loading, data }) => {
            if (loading) {
              return <PageLoader />;
            }
            if (!loading && data) {
              const categories = data.listCategorys.items.map(
                (cat: { id: string; name: string }) => ({
                  value: cat.id,
                  label: cat.name
                })
              );
              return (
                <Row>
                  <Col sm={12} md={12} lg={12}>
                    <Card>
                      <Row>
                        <Col sm={12} md={12} lg={12}>
                          <p>Filters</p>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Keyword Search</Label>
                          <Input
                            type="text"
                            name="keywordSearch"
                            value={keywordSearch}
                            id="keywordSearch"
                            placeholder="Keyword Search"
                            onChange={e =>
                              this.setState({ keywordSearch: e.target.value })
                            }
                          />
                          <br />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Category</Label>
                          <Select
                            options={categories}
                            onChange={this.selectCategory}
                            value={category}
                            placeholder=""
                            className="select-styling"
                          />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Province</Label>
                          <Select
                            options={this.listOfProvinces(Province)}
                            onChange={this.selectProvince}
                            value={province}
                            placeholder=""
                            className="select-styling"
                          />
                          <br />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={4} lg={4}>
                          <Label>City</Label>
                          <Input
                            type="text"
                            name="city"
                            value={city}
                            id="city"
                            placeholder="City Search"
                            onChange={e =>
                              this.setState({ city: e.target.value })
                            }
                          />
                          <br />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Suburb</Label>
                          <Select
                            options={this.listOfSuburbs(Suburb)}
                            onChange={this.selectSuburb}
                            value={suburb}
                            placeholder=""
                            className="select-styling"
                          />
                          <br />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={12} lg={12}>
                          <StyledButton
                            type="button"
                            label="Filter"
                            width="120px"
                            color={Colors.snow}
                            background={Colors.default}
                            margin="5px"
                          />
                          <StyledButton
                            type="reset"
                            label="Clear Filters"
                            width="120px"
                            color={Colors.coal}
                            background={Colors.background}
                            margin="5px"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              );
            } else {
              return <div>There has been an error loading filters</div>;
            }
          }}
        </Query>
        <Query
          query={gql(getCompanyReports)}
          variables={{
            limit: TableConsts.limit,
            id: getUserId()
          }}
        >
          {({ loading, error, data }) => {
            if (loading) {
              return <PageLoader />;
            }

            if (error) {
              return <div>There was a problem loading your data</div>;
            }

            if (!data || !data.getUser.company.items[0].transactions) {
              return <Row>There are no Reports at the moment</Row>;
            }

            return (
              <React.Fragment>
                <TableHeaderContainer>
                  <TableHeader>
                    <span>Purchased Reports</span>
                  </TableHeader>
                </TableHeaderContainer>
                <Table
                  data={data.getUser.company.items[0].transactions.report}
                  columns={[
                    {
                      Header: "Report Name",
                      accessor: "name",
                      sortable: true
                    },
                    {
                      Header: "Category",
                      accessor: "category",
                      sortable: true
                    },
                    {
                      Header: "Price",
                      accessor: "price",
                      sortable: true
                    },
                    {
                      Header: "Published Date",
                      accessor: "datePublished",
                      sortable: false,
                      filterable: false
                    },
                    {
                      Header: "Purchases",
                      accessor: "placeholder",
                      sortable: false,
                      filterable: false
                    },
                    {
                      Header: "Status",
                      accessor: "status",
                      sortable: true,
                      filterable: false
                    }
                  ]}
                  defaultPageSize={TableConsts.defaultRows}
                  showPaginationBottom={
                    data.getUser.company.items[0].transactions.report.items
                      .length > TableConsts.defaultRows
                  }
                />
              </React.Fragment>
            );
          }}
        </Query>
        <Helmet title="View Purchased Reports" />
      </BackendWrapper>
    );
  }
}

export default withToastManager(PurchasedReports);



// WEBPACK FOOTER //
// ./src/Containers/PurchasedReports/index.tsx